import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useOutlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlass, Spinner, X, Users, Scroll, Folder } from '@phosphor-icons/react';
import { Sidebar } from './Sidebar';
import { dashboardService } from '../../../services/dashboard.service';
import type { SearchResults } from '../../../services/dashboard.service';
import { authService } from '../../../services/auth.service';
import { useHaptic } from '../../../hooks/useHaptic';
import { SCROLL_CONTAINER_ID } from '../../../hooks/useScrollRestoration';
import { toast } from 'sonner';
import posthog from '../../../posthog';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const { lightTap } = useHaptic();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auth check on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate, location.pathname]);

  // Ctrl+K / Cmd+K focus hotkey
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
        lightTap();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightTap]);

  // Debounced search logic
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true);
        try {
          const data = await dashboardService.searchAll(searchQuery.trim());
          setSearchResults(data);
        } catch (err: any) {
          console.error(err);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close search on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchResults(null);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch { /* ignore */ }
    posthog.reset();
    localStorage.removeItem('token');
    localStorage.removeItem('name');
    localStorage.removeItem('role');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleSearchResultClick = (type: string, id?: string) => {
    lightTap();
    setSearchQuery('');
    setSearchResults(null);
    if (type === 'alumni' && id) {
      navigate(`/alumni/${id}`);
    } else if (type === 'alumni') {
      navigate('/alumni');
    } else if (type === 'proposals') {
      navigate('/proposals');
    } else if (type === 'projects') {
      navigate('/projects');
    }
  };

  const clearSearch = () => {
    lightTap();
    setSearchQuery('');
    setSearchResults(null);
  };

  return (
    <div className={`h-screen overflow-hidden flex transition-colors duration-500 ${location.pathname.includes('/chat') ? 'bg-chat-mesh' : 'bg-app-bg'}`}>
      <Sidebar onLogout={handleLogout} />

      <div className="flex-1 flex flex-col min-w-0 md:ml-20">
        {/* ── Top Bar ── */}
        {!location.pathname.includes('/chat') && (
          <header className="top-0 z-20 px-6 py-3 flex items-center justify-between gap-4">
          {/* Search bar wrapper */}
          <div className="relative w-full max-w-md" ref={searchRef}>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <MagnifyingGlass size={16} weight="bold" />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search alumni, proposals, projects... (Ctrl+K)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-surface-hover border border-border focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-xl pl-10 pr-10 py-2.5 text-xs text-primary placeholder-slate-400 transition-smooth shadow-sm"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                {isSearching && (
                  <Spinner size={14} className="text-accent animate-spin" />
                )}
                {searchQuery && !isSearching && (
                  <button
                    onClick={clearSearch}
                    className="p-0.5 rounded-md hover:bg-slate-200/60 text-slate-450 hover:text-slate-700 transition-colors cursor-pointer"
                  >
                    <X size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>

            {/* Search Dropdown with Framer Motion */}
            <AnimatePresence>
              {searchResults && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute top-full mt-2 left-0 right-0 glass-dropdown z-50 overflow-hidden font-sans"
                >
                  <div className="p-4 max-h-[380px] overflow-y-auto">
                    <div className="flex items-center justify-between mb-3 border-b border-slate-50 pb-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Results for "{searchQuery}"</span>
                      <button onClick={clearSearch} className="text-[10px] font-bold text-slate-400 hover:text-accent transition-colors cursor-pointer">Clear</button>
                    </div>
                    <div className="space-y-4">
                      {['alumni', 'proposals', 'projects'].map((type) => {
                        const items = (searchResults as any)[type] as any[];
                        if (!items || !items.length) return null;
                        return (
                          <div key={type} className="space-y-1.5">
                            <p className="text-[9px] font-bold text-slate-350 uppercase tracking-widest flex items-center gap-1.5">
                              {type === 'alumni' && <Users size={12} />}
                              {type === 'proposals' && <Scroll size={12} />}
                              {type === 'projects' && <Folder size={12} />}
                              <span>{type} ({items.length})</span>
                            </p>
                            <div className="space-y-1">
                              {items.map((item) => (
                                <div
                                  key={item._id}
                                  onClick={() => handleSearchResultClick(type, item.donor_id || item._id)}
                                  className="px-3 py-2 rounded-xl hover:bg-accent-light hover:text-accent-hover cursor-pointer transition-colors"
                                >
                                  <p className="text-xs font-bold text-slate-800 truncate">
                                    {item.source_info?.current_name || item.source_info?.csv_name || item.title || item.project_title}
                                  </p>
                                  <p className="text-[10px] text-slate-400 truncate mt-0.5 font-medium">
                                    {item.source_info
                                      ? `${item.source_info.designation || 'Alumni'} @ ${item.source_info.company || 'N/A'}`
                                      : item.domain
                                      ? `${item.domain} • ${item.budget_lakhs ? `${item.budget_lakhs}L` : 'TBD'}`
                                      : `ID: ${item.project_id}`}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          </header>
        )}

        {/* ── Page Content ── */}
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
          {(location.pathname.includes('proposal-matcher') || location.pathname.includes('/chat')) ? (
            <div className="flex-1 flex flex-col h-full w-full min-h-0 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
              {outlet}
            </div>
          ) : (
            <div id={SCROLL_CONTAINER_ID} className="flex-1 overflow-y-auto p-6 lg:p-8 animate-in fade-in slide-in-from-bottom-2 duration-300 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="w-full max-w-[1920px] mx-auto space-y-8">
                {outlet}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};